import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { 
  Check, Home, FileText, PenTool, Users, Building2,
  DollarSign, MessageSquare, Mail, Car, Wrench,
  Sparkles, ArrowRight, TrendingUp, Shield, Clock
} from 'lucide-react';

function RealEstatePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const heroFeatures = [
    "Pipeline de Propiedades",
    "Contratos & Firma Digital",
    "Gestión de Arriendos",
    "Documentación Centralizada"
  ];

  const coreModules = [
    {
      icon: TrendingUp,
      title: "Pipeline Inmobiliario",
      description: "Gestiona leads, potenciales y cierra ventas más rápido con cotizaciones automáticas y seguimiento inteligente.",
      modules: ["Leads", "Embudo de venta", "Cotizaciones", "Agentes IA"]
    },
    {
      icon: FileText,
      title: "Contratos Digitales",
      description: "Genera, gestiona y firma contratos con firma digital simple y avanzada. Todo 100% legal y trazable.",
      modules: ["Gestión de contratos", "Firma digital", "Gestión de documentos"]
    },
    {
      icon: Home,
      title: "Gestión de Propiedades",
      description: "Administra todo tu portafolio: propiedades, parking, documentación y órdenes técnicas desde un solo lugar.",
      modules: ["Gestión de espacios", "Gestión de parking", "Ordenes técnicas"]
    },
    {
      icon: Users,
      title: "CRM Inmobiliario",
      description: "Base de datos completa de clientes, proveedores y propietarios con historial de interacciones.",
      modules: ["Gestión de Clientes", "Gestión de proveedores", "WhatsApp Business"]
    },
    {
      icon: MessageSquare,
      title: "Comunicación Efectiva",
      description: "WhatsApp Business integrado, mailing masivo y seguimiento automatizado de leads.",
      modules: ["WhatsApp Business", "Mailing Masivo", "Agentes IA"]
    },
    {
      icon: DollarSign,
      title: "Facturación & Cobranza",
      description: "Facturación electrónica para Chile, Perú y Colombia. Flujo de cobranza automatizado.",
      modules: ["Facturación electrónica", "Flujo de cobranza", "Conciliación bancaria"]
    }
  ];

  const benefits = [
    {
      title: "Cierra Más Rápido",
      description: "Pipeline optimizado y agentes IA que cualifican leads automáticamente. Reduce el ciclo de ventas en 50%.",
      icon: Clock
    },
    {
      title: "Documentación 100% Digital",
      description: "Olvídate del papel. Contratos digitales con firma electrónica válida legalmente.",
      icon: Shield
    },
    {
      title: "Gestión Centralizada",
      description: "Todas tus propiedades, clientes y documentos en un solo lugar accesible 24/7.",
      icon: Building2
    },
    {
      title: "Automatización Total",
      description: "Cotizaciones automáticas, seguimiento de leads y recordatorios. Ahorra 30 horas mensuales.",
      icon: Sparkles
    }
  ];

  const includedModules = [
    "✅ Leads & Pipeline de Ventas",
    "✅ Cotizaciones Automáticas",
    "✅ Gestión de Contratos Digitales",
    "✅ Firma Digital Simple y Avanzada",
    "✅ CRM Completo (Clientes & Proveedores)",
    "✅ Gestión de Propiedades",
    "✅ Gestión de Parking",
    "✅ WhatsApp Business Integrado",
    "✅ Agentes Comerciales de IA",
    "✅ Mailing Masivo",
    "✅ Gestión de Documentos",
    "✅ Órdenes Técnicas",
    "✅ Facturación Electrónica",
    "✅ Flujo de Cobranza",
    "✅ Conciliación Bancaria",
    "✅ Impresión Directa"
  ];

  return (
    <>
      <SEOHead
        title="Cloudbase para Inmobiliarias - CRM & Gestión Integral"
        description="Software completo para inmobiliarias: pipeline de ventas, contratos digitales, gestión de propiedades y más."
        keywords="software inmobiliario, CRM inmobiliaria, gestión propiedades, contratos digitales"
        path="/inmobiliarias"
        breadcrumbs={[
          { name: 'Inicio', item: '/' },
          { name: 'Industrias', item: '/#industrias' },
          { name: 'Inmobiliarias', item: '/inmobiliarias' }
        ]}
      />
      <div className="min-h-screen bg-white">
        <Header />

        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-black relative overflow-hidden min-h-screen flex items-center">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-black"></div>
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-5xl mx-auto text-center">
              <div className="inline-block mb-6 px-4 py-2 bg-green-600/20 border border-green-600/30 text-green-400 rounded-full text-sm font-semibold">
                Solución para Inmobiliarias
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-8 leading-tight text-white">
                Plataforma Integral para tu Inmobiliaria
              </h1>
              
              <p className="text-xl sm:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed font-light">
                Del lead al cierre. Gestiona propiedades, clientes, contratos digitales y facturación en una sola plataforma diseñada para inmobiliarias.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
                <Button 
                  size="lg" 
                  className="bg-green-600 hover:bg-green-700 text-white px-10 py-7 text-base rounded font-semibold transition-all duration-200"
                  onClick={() => window.location.href = '/demo'}
                >
                  Ver Demo Personalizada
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-black px-10 py-7 text-base rounded font-semibold transition-all duration-200"
                  onClick={() => window.location.href = '/demo'}
                >
                  Hablar con un Experto
                </Button>
              </div>

              {/* Trust indicators */}
              <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-400">
                {heroFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Core Modules Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Pack Completo para Inmobiliarias
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Todo lo que necesitas para gestionar tu negocio inmobiliario
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {coreModules.map((module, index) => {
                const Icon = module.icon;
                return (
                  <div
                    key={index}
                    className="p-8 bg-gray-50 rounded-lg hover:shadow-lg transition-shadow"
                  >
                    <Icon className="w-12 h-12 text-green-600 mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {module.title}
                    </h3>
                    <p className="text-gray-600 mb-4">{module.description}</p>
                    <div className="space-y-2">
                      {module.modules.map((mod, idx) => (
                        <div key={idx} className="text-sm text-green-600 font-medium">
                          • {mod}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Beneficios Comprobados
              </h2>
              <p className="text-xl text-gray-600">
                Resultados que impactan directamente en tu negocio
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div key={index} className="text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                      <Icon className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Modules Included */}
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Todo Incluido en tu Plan
              </h2>
              <p className="text-xl text-gray-600">
                Plataforma completa sin módulos adicionales
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {includedModules.map((module, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg"
                >
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0" />
                  <span className="text-gray-900 font-medium">{module}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-green-600 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              ¿Listo para digitalizar tu inmobiliaria?
            </h2>
            <p className="text-xl mb-8 text-white/90">
              Agenda una demo y descubre cómo podemos ayudarte a cerrar más negocios.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-white text-green-600 hover:bg-gray-100 px-10 py-7 text-base rounded font-semibold transition-all duration-200 inline-flex items-center gap-2"
                onClick={() => window.location.href = '/demo'}
              >
                Agendar Demo Gratuita
                <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
            <p className="mt-8 text-white/70 text-sm">
              Implementación rápida • Capacitación incluida • Soporte dedicado
            </p>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}

export default RealEstatePage;